import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import * as z from "zod";
import { Brand } from "@/types/brand";
import { PlusCircle, Trash2 } from "lucide-react";

const formSchema = z.object({
  competitors: z
    .array(
      z.object({
        name: z.string().min(1, "Competitor name is required"),
        url: z.string().url("Please enter a valid URL"),
      }),
    )
    .min(0),
});

type FormValues = z.infer<typeof formSchema>;

interface CompetitorsFormProps {
  data: Brand;
  onSubmit: (data: Partial<Brand>) => void;
  mode: "onboarding" | "edit";
  onBack?: () => void; // Optional for onboarding mode
}

export function CompetitorsForm({
  data,
  onSubmit,
  mode,
  onBack,
}: CompetitorsFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      competitors:
        data.competitors && data.competitors.length > 0 ? data.competitors : [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "competitors",
  });

  const handleFormSubmit = (values: FormValues) => {
    onSubmit(values);
  };

  const renderTitle = () => {
    if (mode === "onboarding") {
      return (
        <>
          <CardTitle>Competitors Analysis (Optional)</CardTitle>
          <CardDescription>
            Add competitors to help us understand your market position and
            create more targeted content
          </CardDescription>
        </>
      );
    }
    return null; // In edit mode, the title is handled by the parent component
  };

  const renderButtons = () => {
    if (mode === "onboarding") {
      return (
        <div className="flex justify-between pt-4">
          {onBack && (
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onSubmit({ competitors: [] })}
            >
              Skip
            </Button>
            <Button type="submit">Continue</Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-end pt-4">
        <Button type="submit" className="bg-black hover:bg-black/90">
          Save
        </Button>
      </div>
    );
  };

  const formContent = (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="space-y-6"
      >
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-start space-x-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                <FormField
                  control={form.control}
                  name={`competitors.${index}.name`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={index !== 0 ? "sr-only" : ""}>
                        Competitor Name
                      </FormLabel>
                      <FormControl>
                        <Input placeholder="Competitor Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`competitors.${index}.url`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={index !== 0 ? "sr-only" : ""}>
                        Competitor Website
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://competitor.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div
                className="flex"
                style={{ marginTop: index === 0 ? "2rem" : "0.5rem" }}
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(index)}
                  disabled={false}
                  title="Remove competitor"
                >
                  <Trash2 className="h-5 w-5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {fields.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => append({ name: "", url: "" })}
            disabled={fields.length >= 5}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Another Competitor
          </Button>
        )}

        {fields.length === 0 && (
          <div className="text-center py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => append({ name: "", url: "" })}
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Your First Competitor
            </Button>
          </div>
        )}

        {fields.length > 0 && (
          <FormDescription className="text-center mt-4">
            Add 3-5 competitors to help us position your content effectively
            (optional)
          </FormDescription>
        )}

        {renderButtons()}
      </form>
    </Form>
  );

  // In onboarding mode, wrap in Card, in edit mode, return just the form
  if (mode === "onboarding") {
    return (
      <Card className="w-full max-w-3xl mx-auto">
        <CardHeader>{renderTitle()}</CardHeader>
        <CardContent>{formContent}</CardContent>
      </Card>
    );
  }

  return formContent;
}
